import { Box, Stack } from '@mui/material';
import React, { Fragment, useMemo, useState } from 'react';
import ElementDataRow from 'components/common/element-data/ElementDataRow';
import { ElementMetadataFieldName, IElementDataDocuments } from 'components/common/element-data/types';
import useBundleTranslation from 'i18n';
import { useQuery } from '@tanstack/react-query';
import { ElementDocumentType } from 'components/element-viewer';
import { homePage } from 'api/home-page';
import styles from '../ElementData.styles';
import { instance } from 'api/api';
import IconMi from 'components/common/icon/IconMi';
import { MenuDocument } from 'components/element-viewer/controls-panel-icon/DocumentsButton';
import { getFieldElementMetadataSettings } from 'components/common/element-data/functions';

export default function Documents(props: IElementDataDocuments) {
    const { t } = useBundleTranslation(['components/common/element_data']);
    const { elementId = 0, documentsNeedToLoad = false, documents: documentsData = [], settings } = props;
    const fieldKey: ElementMetadataFieldName = 'documents';
    const { fieldMetadataDisplay, fieldMetadataOrder } = getFieldElementMetadataSettings(settings, fieldKey);

    const [open, setOpen] = useState(false);

    const { data: loadedDocuments, isLoading } = useQuery<Array<ElementDocumentType>, Error>({
        queryKey: [`tile-${elementId}-documents`],
        queryFn: () => {
            return homePage.loadDocuments(elementId);
        },
        enabled: fieldMetadataDisplay && documentsNeedToLoad,
    });

    const documents: Array<ElementDocumentType> = useMemo(() => {
        if (documentsNeedToLoad) {
            if (isLoading) return [];
            return loadedDocuments ?? [];
        }
        return documentsData;
    }, [loadedDocuments, isLoading, documentsData]);

    if (!fieldMetadataDisplay || documents.length == 0) {
        return null;
    }

    return (
        <ElementDataRow order={fieldMetadataOrder}>
            <Box
                width={1}
                sx={styles.collapseExpandItem}
                className={`collapse-expand-item collapse-expand-item--state-${open ? 'expand' : 'collapse'}`}
            >
                <Stack
                    direction="row"
                    className={'collapse-expand-item__header'}
                    onClick={() => {
                        if (!open) {
                            instance.post('data/content/mark-docs-as-viewed', {
                                docIds: documents.map((doc) => doc.documentId),
                            });
                        }
                        setOpen((prevState) => !prevState);
                    }}
                >
                    <Box sx={styles.labelIcon}>
                        <IconMi icon="paperclip" />
                    </Box>

                    <Box flexGrow={1}>{t(`element_data_label.${fieldKey}`)}</Box>
                    <Box sx={{ ...styles.labelIcon, ...{ mr: 0, ml: 1 } }}>
                        <IconMi icon={open ? 'chevron-down' : 'chevron-right'} sx={{ fontSize: 16 }} />
                    </Box>
                </Stack>
                <Stack
                    direction="column"
                    flexWrap={'wrap'}
                    sx={{ pl: '24px' }}
                    className={'collapse-expand-item__children'}
                >
                    {documents.map((doc, index) => (
                        <Fragment key={doc.documentId}>
                            {(index == 0 || documents[index].documentType != documents[index - 1].documentType) && (
                                <Box sx={{ pt: index != 0 ? 1 : 0.5 }}>{doc.documentType}</Box>
                            )}
                            <Box sx={{ mt: 0.5 }}>
                                <MenuDocument doc={doc} />
                            </Box>
                        </Fragment>
                    ))}
                </Stack>
            </Box>
        </ElementDataRow>
    );
}
